import type { Clip } from "../api/types";

/** All clip events are on the CFR working copy, including a re-ingested segment.
 * segment.start_ms is a display offset into the upload; never add it to media seeks.
 */
export function clipAnchor(c: Clip): number {
  const release = c.events_ms.release;
  return Math.max(0, release != null && Number.isFinite(release) ? release : c.window_ms.start);
}

/** Only this API URL authorizes the whole working copy. A clip-cut URL is not a match source. */
export function matchSource(clips: Clip[], videoId: string): string | null {
  const expected = `/v1/videos/${encodeURIComponent(videoId)}/media/working`;
  return clips.some((c) => c.clip.playback_url?.split("#")[0] === expected) ? expected : null;
}

/** A member's cut file starts at zero; its source clock starts at the clip window.
 * Managed working-copy fragments already use source seconds and need no offset.
 */
export function clipMediaOffset(clip: Clip | null, src: string | null): number {
  return clip && src && /^\/v1\/deliveries\/[^/]+\/playback(?:\?[^#]*)?$/.test(src) ? clip.window_ms.start : 0;
}

/** Earlier source time wins ties; original order resolves coincident anchors. */
export function nearestClip(clips: Clip[], ms: number): number | null {
  let best: number | null = null;
  for (let k = 0; k < clips.length; k++) {
    const anchor = clipAnchor(clips[k]!);
    if (best == null || Math.abs(anchor - ms) < Math.abs(clipAnchor(clips[best]!) - ms)
      || (Math.abs(anchor - ms) === Math.abs(clipAnchor(clips[best]!) - ms) && anchor < clipAnchor(clips[best]!))) best = k;
  }
  return best;
}

export function timelineClock(ms: number): string {
  const s = Math.max(0, Math.floor(Math.round(ms) / 1000));
  return `${s >= 3600 ? `${Math.floor(s / 3600)}:` : ""}${String(Math.floor(s / 60) % 60).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
